import React, { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import type { Job, EmploymentType, ExperienceLevel } from '../types'
import {
  EMPLOYMENT_TYPE_LABELS, EXPERIENCE_LEVEL_LABELS, SALARY_LABELS,
} from '../constants/profileOptions'
import {
  CheckCircle, FileText, Lock, Briefcase, MapPin, Building2, User, ExternalLink,
  Search, X, Upload, Send,
  Eye, Star, Calendar, SlidersHorizontal, DollarSign, Clock, GraduationCap,
  Sparkles, Target, BarChart3, TrendingUp, Info, PartyPopper, Bell
} from 'lucide-react'
import toast from 'react-hot-toast'
import { notifyNewApplicationEmail } from '../utils/emailNotify'
import InfoTooltip from '../components/InfoTooltip'

const COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000

/* ──────────────────────────── helpers ──────────────────────────── */

const TYPE_LABELS = EMPLOYMENT_TYPE_LABELS
const LEVEL_LABELS = EXPERIENCE_LEVEL_LABELS

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

/* ───────────────────── Apply Modal ─────────────────────────────── */

interface ApplyModalProps {
  job: Job
  isInvited?: boolean
  onClose: () => void
  onApplied: () => void
}

const ApplyModal: React.FC<ApplyModalProps> = ({ job, isInvited, onClose, onApplied }) => {
  const { user } = useAuthStore()
  const [profile, setProfile] = useState<{
    first_name?: string; last_name?: string; program?: string
    department?: string; year_graduated?: number; skills_acquired?: string[]; phone?: string
  } | null>(null)
  const [coverLetter, setCoverLetter] = useState('')
  const [resumeFile, setResumeFile] = useState<File | null>(null)
  const [phone, setPhone] = useState<string | undefined>(undefined)
  const [portfolioUrl, setPortfolioUrl] = useState('')
  const [expectedSalary, setExpectedSalary] = useState('')
  const [availability, setAvailability] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!user?.id) return
    supabaseClient
      .from('alumni_profiles')
      .select('first_name, last_name, program, department, year_graduated, skills_acquired, phone')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          setProfile(data)
          if (data.phone) setPhone(data.phone)
        }
      })
  }, [user?.id])

  const handleSubmit = async () => {
    if (!user?.id) return
    if (!resumeFile) {
      toast.error('Please upload your resume before submitting')
      return
    }
    setSubmitting(true)
    try {
      let resumeUrl: string | undefined
      if (resumeFile) {
        const ext = resumeFile.name.split('.').pop()
        const filePath = `${user.id}/${job.id}.${ext}`
        const { error: uploadErr } = await supabaseClient.storage
          .from('resumes')
          .upload(filePath, resumeFile, { upsert: true })
        if (uploadErr) {
          console.error('[APPLY] Resume upload failed:', uploadErr)
          throw new Error(`Resume upload failed: ${uploadErr.message}`)
        }
        resumeUrl = filePath
      }

      const applicationData: Record<string, any> = {}
      if (phone) applicationData.phone = phone
      if (portfolioUrl.trim()) applicationData.portfolio_url = portfolioUrl.trim()
      if (expectedSalary) applicationData.expected_salary = expectedSalary
      if (availability) applicationData.availability = availability

      const rpcArgs: Record<string, any> = {
        p_job_id: job.id,
        p_cover_letter: coverLetter || null,
        p_resume_url: resumeUrl || null,
      }
      if (Object.keys(applicationData).length > 0) {
        rpcArgs.p_application_data = applicationData
      }

      console.log('[APPLY] Calling apply_for_job RPC with args:', rpcArgs)
      const { error } = await supabaseClient.rpc('apply_for_job', rpcArgs as any)
      if (error) {
        console.error('[APPLY] RPC apply_for_job failed:', error)
        throw error
      }
      toast.success('Application submitted!')
      notifyNewApplicationEmail(job.id)
      onApplied()
    } catch (err: any) {
      const msg = err?.message || 'Failed to apply'
      console.error('[APPLY] Full error:', err)
      if (msg.includes('already have an active application')) {
        toast.error('You already have an active application for this job')
      } else if (msg.includes('can re-apply') || msg.includes('re-apply to this job after')) {
        toast.error(msg)
      } else if (msg.includes('duplicate') || msg.includes('unique')) {
        toast.error('You have already applied to this job')
      } else {
        toast.error(msg)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const fullName = profile
    ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
    : ''

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Apply for Position</h2>
            <p className="text-sm text-gray-500 mt-0.5">{job.title} at {job.company_name}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Invitation callout */}
          {isInvited && (
            <div className="flex items-start gap-2.5 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3">
              <Info className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
              <p className="text-sm text-indigo-800">
                <span className="font-semibold">You have a pending invitation for this job.</span>{' '}
                Submitting this application will automatically accept it.
              </p>
            </div>
          )}

          {/* Applying as — profile summary */}
          {profile && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <p className="text-[11px] font-semibold text-blue-600 uppercase tracking-wide mb-2.5">Applying as</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-900 text-white flex items-center justify-center font-bold text-sm shrink-0">
                  {fullName.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900">{fullName || 'Your Name'}</p>
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                    {profile.program && <span>{profile.program}</span>}
                    {profile.year_graduated && <span>Class of {profile.year_graduated}</span>}
                    {profile.department && <span className="text-gray-400">·</span>}
                    {profile.department && <span>{profile.department}</span>}
                  </div>
                </div>
              </div>
              {(profile.skills_acquired?.length || 0) > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {profile.skills_acquired!.slice(0, 6).map(s => (
                    <span key={s} className="bg-white border border-blue-200 text-blue-700 text-[11px] font-medium px-2 py-0.5 rounded-md">{s}</span>
                  ))}
                  {(profile.skills_acquired?.length || 0) > 6 && (
                    <span className="bg-white border border-blue-200 text-gray-500 text-[11px] px-2 py-0.5 rounded-md">+{profile.skills_acquired!.length - 6} more</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Cover Letter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Cover Letter <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              rows={4}
              value={coverLetter}
              onChange={e => setCoverLetter(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Tell the employer why you're a great fit for this role..."
            />
          </div>

          {/* Resume */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Resume <span className="text-red-500">*</span> <span className="text-gray-400 font-normal">(PDF, max 5 MB)</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer border-2 border-dashed border-gray-300 rounded-xl p-4 hover:border-blue-400 hover:bg-blue-50/30 transition">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <Upload className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">
                  {resumeFile ? resumeFile.name : 'Click to upload your resume'}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">PDF format only</p>
              </div>
              <input
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={e => {
                  const f = e.target.files?.[0]
                  if (f && f.size > 5 * 1024 * 1024) { toast.error('File must be under 5 MB'); return }
                  setResumeFile(f || null)
                }}
              />
            </label>
          </div>

          {/* Additional Details */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-3">
              Additional Details <span className="text-gray-400 font-normal">(all optional)</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone Number</label>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 bg-white">
                  <PhoneInput
                    international
                    defaultCountry="PH"
                    value={phone}
                    onChange={setPhone}
                    className="phone-input-custom w-full"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Portfolio / LinkedIn</label>
                <input
                  type="url"
                  value={portfolioUrl}
                  onChange={e => setPortfolioUrl(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://linkedin.com/in/..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Expected Salary</label>
                <select
                  value={expectedSalary}
                  onChange={e => setExpectedSalary(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
                >
                  <option value="">Not specified</option>
                  <option value="under_20k">Under ₱20,000</option>
                  <option value="20k_40k">₱20,000 – ₱40,000</option>
                  <option value="40k_60k">₱40,000 – ₱60,000</option>
                  <option value="60k_80k">₱60,000 – ₱80,000</option>
                  <option value="80k_100k">₱80,000 – ₱100,000</option>
                  <option value="over_100k">Over ₱100,000</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Availability</label>
                <select
                  value={availability}
                  onChange={e => setAvailability(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
                >
                  <option value="">Not specified</option>
                  <option value="immediately">Immediately available</option>
                  <option value="two_weeks">2 weeks notice</option>
                  <option value="one_month">1 month notice</option>
                  <option value="three_months">3+ months</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t bg-gray-50 rounded-b-2xl shrink-0">
          <button onClick={onClose} className="px-4 py-2.5 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-100 transition">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-5 py-2.5 text-sm font-medium bg-blue-900 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50 flex items-center gap-2 transition"
          >
            <Send className="w-4 h-4" />
            {submitting ? 'Submitting...' : 'Submit Application'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

/* ─────────────────── View Job Modal ─────────────────────────────── */

interface ViewJobModalProps {
  job: Job
  applied: boolean
  cooldownDate?: string
  onClose: () => void
  onApply: () => void
}

const ViewJobModal: React.FC<ViewJobModalProps> = ({ job, applied, cooldownDate, onClose, onApply }) => createPortal(
  <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-6 border-b">
        <div className="flex items-start gap-3.5 flex-1 min-w-0">
          {job.company_logo ? (
            <img src={job.company_logo} alt={job.company_name} className="w-12 h-12 rounded-xl object-cover shrink-0 border border-gray-100" />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-blue-900 text-white flex items-center justify-center text-xl font-bold shrink-0">
              {job.company_name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-gray-900">{job.title}</h2>
              {job.is_partner_listing && (
                <span className="bg-yellow-400 text-gray-900 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5">
                  <Star className="w-3 h-3" /> PARTNER
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-0.5">{job.company_name}</p>
            {job.avg_rating != null && (
              <div className="flex items-center gap-1 mt-1">
                {[1,2,3,4,5].map(s => (
                  <Star
                    key={s}
                    className="w-3.5 h-3.5"
                    fill={s <= Math.round(job.avg_rating!) ? '#FBBF24' : 'none'}
                    stroke={s <= Math.round(job.avg_rating!) ? '#FBBF24' : '#D1D5DB'}
                  />
                ))}
                <span className="text-xs text-gray-500 ml-0.5">{Number(job.avg_rating).toFixed(1)}</span>
                {job.rating_count != null && (
                  <span className="text-xs text-gray-400">({job.rating_count} {job.rating_count === 1 ? 'review' : 'reviews'})</span>
                )}
              </div>
            )}
            {job.employer_name && (
              <p className="text-xs text-gray-400 mt-0.5">Posted by {job.employer_name}</p>
            )}
          </div>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg shrink-0"><X className="w-5 h-5 text-gray-400" /></button>
      </div>

      <div className="p-6 space-y-5">
        {/* Info badges */}
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-800 text-xs font-medium px-3 py-1.5 rounded-lg">
            <Briefcase className="w-3.5 h-3.5" /> {TYPE_LABELS[job.employment_type]}
          </span>
          <span className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-800 text-xs font-medium px-3 py-1.5 rounded-lg">
            <GraduationCap className="w-3.5 h-3.5" /> {LEVEL_LABELS[job.experience_level]}
          </span>
          {job.salary_range && (
            <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-800 text-xs font-medium px-3 py-1.5 rounded-lg">
              <DollarSign className="w-3.5 h-3.5" /> {SALARY_LABELS[job.salary_range] || job.salary_range}
            </span>
          )}
          {job.location && (
            <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 text-xs font-medium px-3 py-1.5 rounded-lg">
              <MapPin className="w-3.5 h-3.5" /> {job.location}
            </span>
          )}
        </div>

        {/* Dates */}
        <div className="flex flex-wrap gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Posted: {formatDate(job.posted_at)}</span>
          {job.deadline && (
            <span className={`flex items-center gap-1 ${new Date(job.deadline) < new Date() ? 'text-red-500' : 'text-orange-600'}`}>
              <Calendar className="w-3.5 h-3.5" /> Deadline: {formatDate(job.deadline)}
            </span>
          )}
        </div>

        {/* Description */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Job Description</h3>
          <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{job.description}</p>
        </div>

        {/* Requirements */}
        {job.requirements && job.requirements.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Requirements</h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
              {job.requirements.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}

        {/* Skills */}
        {job.skills_required && job.skills_required.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Required Skills</h3>
            <div className="flex flex-wrap gap-2">
              {job.skills_required.map(s => (
                <span key={s} className="bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-lg">{s}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-2xl">
        <button onClick={onClose} className="px-4 py-2.5 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-100 transition">Close</button>
        {applied ? (
          <span className="px-4 py-2.5 text-sm font-semibold text-green-600 flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4" /> Already Applied
          </span>
        ) : cooldownDate ? (
          <span className="px-4 py-2.5 text-sm font-semibold text-orange-600 flex items-center gap-1.5">
            <Clock className="w-4 h-4" /> Re-apply {cooldownDate}
          </span>
        ) : (
          <button
            onClick={() => { onClose(); onApply() }}
            className="px-5 py-2.5 text-sm font-medium bg-blue-900 text-white rounded-lg hover:bg-blue-800 flex items-center gap-2 transition"
          >
            <Send className="w-4 h-4" /> Apply Now
          </button>
        )}
      </div>
    </div>
  </div>,
  document.body
)

/* ─────────────────── Company Info Modal ────────────────────────── */

interface CompanyInfoModalProps {
  job: Job
  onClose: () => void
}

const CompanyInfoModal: React.FC<CompanyInfoModalProps> = ({ job, onClose }) => createPortal(
  <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b">
        <h2 className="text-base font-bold text-gray-900">About This Company</h2>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition">
          <X className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      <div className="p-5 space-y-5">
        {/* Company logo + name */}
        <div className="flex items-center gap-4">
          {job.company_logo ? (
            <img src={job.company_logo} alt={job.company_name} className="w-16 h-16 rounded-2xl object-cover border border-gray-100 shrink-0" />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-blue-900 text-white flex items-center justify-center text-2xl font-bold shrink-0">
              {job.company_name.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <h3 className="text-lg font-bold text-gray-900">{job.company_name}</h3>
            {job.company_industry && <p className="text-sm text-gray-500 mt-0.5">{job.company_industry}</p>}
            {job.avg_rating != null && (
              <div className="flex items-center gap-1 mt-1">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} className="w-3.5 h-3.5"
                    fill={s <= Math.round(job.avg_rating!) ? '#FBBF24' : 'none'}
                    stroke={s <= Math.round(job.avg_rating!) ? '#FBBF24' : '#D1D5DB'}
                  />
                ))}
                <span className="text-xs text-gray-500 ml-0.5">{Number(job.avg_rating).toFixed(1)}</span>
                {job.rating_count != null && (
                  <span className="text-xs text-gray-400 ml-0.5">({job.rating_count} {job.rating_count === 1 ? 'review' : 'reviews'})</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Info badges */}
        {(job.company_location || job.company_size_text) && (
          <div className="flex flex-wrap gap-2">
            {job.company_location && (
              <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 text-xs font-medium px-3 py-1.5 rounded-lg">
                <MapPin className="w-3.5 h-3.5" /> {job.company_location}
              </span>
            )}
            {job.company_size_text && (
              <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 text-xs font-medium px-3 py-1.5 rounded-lg">
                <Building2 className="w-3.5 h-3.5" /> {job.company_size_text}
              </span>
            )}
          </div>
        )}

        {/* Description */}
        {job.company_description && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-1.5">About</h4>
            <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{job.company_description}</p>
          </div>
        )}

        {/* Website */}
        {job.company_website && (
          <a
            href={job.company_website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium break-all"
          >
            <ExternalLink className="w-4 h-4 shrink-0" />
            {job.company_website}
          </a>
        )}

        {/* Employer contact */}
        {(job.employer_name || job.employer_email || job.employer_phone) && (
          <div className="border-t pt-4">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2.5">Posted By</h4>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5">
                <User className="w-4 h-4 text-blue-600" />
              </div>
              <div className="min-w-0 space-y-1">
                {job.employer_name && (
                  <p className="text-sm font-semibold text-gray-900">{job.employer_name}</p>
                )}
                {job.employer_position && (
                  <p className="text-xs text-gray-500">{job.employer_position}</p>
                )}
                {!job.employer_position && (
                  <p className="text-xs text-gray-400">Hiring Representative</p>
                )}
                {job.employer_email && (
                  <a
                    href={`mailto:${job.employer_email}`}
                    className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium break-all"
                    onClick={e => e.stopPropagation()}
                  >
                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {job.employer_email}
                  </a>
                )}
                {job.employer_phone && (
                  <a
                    href={`tel:${job.employer_phone}`}
                    className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium"
                    onClick={e => e.stopPropagation()}
                  >
                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {job.employer_phone}
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* If no extra data available */}
        {!job.company_description && !job.company_website && !job.company_industry && !job.company_size_text && !job.company_location && !job.employer_name && !job.employer_email && !job.employer_phone && (
          <p className="text-sm text-gray-400 text-center py-2">No additional company info available.</p>
        )}
      </div>

      <div className="flex justify-end px-5 pb-5">
        <button onClick={onClose} className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition">Close</button>
      </div>
    </div>
  </div>,
  document.body
)

/* ─────────────────── Job Card (Grid) ───────────────────────────── */

interface JobCardProps {
  job: Job
  applied: boolean
  cooldownDate?: string
  isInvited?: boolean
  matchScore?: number
  matchingSkills?: string[]
  onApply: () => void
  onView: () => void
  onCompanyClick: () => void
}

const JobCard: React.FC<JobCardProps> = ({ job, applied, cooldownDate, isInvited, matchScore, matchingSkills, onApply, onView, onCompanyClick }) => {

  // Generate a deterministic color for the company icon
  const iconColors = [
    'bg-blue-900 text-white', 'bg-yellow-400 text-gray-900', 'bg-indigo-600 text-white',
    'bg-emerald-600 text-white', 'bg-rose-600 text-white', 'bg-violet-600 text-white',
  ]
  const colorIdx = job.company_name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % iconColors.length

  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-200 flex flex-col h-full group cursor-pointer"
      onClick={onView}
    >
      <div className="p-5 flex-1 flex flex-col">
        {/* Top: company icon + title + date */}
        <div className="flex items-start gap-3.5">
          {job.company_logo ? (
            <img
              src={job.company_logo}
              alt={job.company_name}
              className="w-11 h-11 rounded-xl object-cover shrink-0 border border-gray-100 cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-blue-400 transition"
              title="View company info"
              onClick={e => { e.stopPropagation(); onCompanyClick() }}
            />
          ) : (
            <div
              className={`w-11 h-11 rounded-xl ${iconColors[colorIdx]} flex items-center justify-center text-lg font-bold shrink-0 cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-blue-400 transition`}
              title="View company info"
              onClick={e => { e.stopPropagation(); onCompanyClick() }}
            >
              {job.company_name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 leading-snug line-clamp-2 group-hover:text-blue-900 transition-colors">
              {job.title}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5 truncate">{job.company_name}</p>
            {job.avg_rating != null && (
              <div className="flex items-center gap-1 mt-0.5">
                {[1,2,3,4,5].map(s => (
                  <Star
                    key={s}
                    className="w-3 h-3"
                    fill={s <= Math.round(job.avg_rating!) ? '#FBBF24' : 'none'}
                    stroke={s <= Math.round(job.avg_rating!) ? '#FBBF24' : '#D1D5DB'}
                  />
                ))}
                <span className="text-xs text-gray-400 ml-0.5">{Number(job.avg_rating).toFixed(1)}</span>
                {job.rating_count != null && (
                  <span className="text-xs text-gray-400">({job.rating_count})</span>
                )}
              </div>
            )}
            {job.employer_name && (
              <p className="text-xs text-gray-400 mt-0.5 truncate">Posted by {job.employer_name}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {job.is_partner_listing && (
              <span className="bg-yellow-400 text-gray-900 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5">
                <Star className="w-3 h-3" /> PARTNER
              </span>
            )}
            {isInvited && (
              <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5">
                <Send className="w-2.5 h-2.5" /> INVITED
              </span>
            )}
          </div>
        </div>

        {/* Location */}
        {job.location && (
          <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5 text-gray-400" /> {job.location}
          </p>
        )}

        {/* Info row: level / type / salary */}
        <div className="flex items-center gap-2 mt-3 text-xs text-gray-600 flex-wrap">
          <span className="font-medium">{LEVEL_LABELS[job.experience_level]}</span>
          <span className="text-gray-300">·</span>
          <span>{TYPE_LABELS[job.employment_type]}</span>
          {job.salary_range && (
            <>
              <span className="text-gray-300">·</span>
              <span className="text-green-700 font-medium">{SALARY_LABELS[job.salary_range] || job.salary_range}</span>
            </>
          )}
        </div>

        {/* Match score */}
        {matchScore != null && matchScore > 0 && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-medium text-gray-600">Match Score</span>
              <span className={`font-bold ${matchScore >= 75 ? 'text-green-600' : matchScore >= 50 ? 'text-blue-600' : 'text-gray-500'}`}>
                {matchScore}%
              </span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${matchScore >= 75 ? 'bg-green-500' : matchScore >= 50 ? 'bg-blue-500' : 'bg-gray-400'}`}
                style={{ width: `${matchScore}%` }}
              />
            </div>
          </div>
        )}

        {/* Description snippet */}
        <p className="text-sm text-gray-600 mt-3 line-clamp-2 leading-relaxed flex-1">
          {job.description}
        </p>

        {/* Skills tags */}
        {job.skills_required && job.skills_required.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {job.skills_required.slice(0, 4).map(s => {
              const matched = matchingSkills?.some(m => m.toLowerCase() === s.toLowerCase())
              return matched ? (
                <span key={s} className="bg-green-100 text-green-700 text-[11px] font-semibold px-2 py-0.5 rounded-md flex items-center gap-0.5">
                  <CheckCircle className="w-2.5 h-2.5" />{s}
                </span>
              ) : (
                <span key={s} className="bg-gray-100 text-gray-700 text-[11px] font-medium px-2 py-0.5 rounded-md">{s}</span>
              )
            })}
            {job.skills_required.length > 4 && (
              <span className="bg-gray-100 text-gray-500 text-[11px] font-medium px-2 py-0.5 rounded-md">+{job.skills_required.length - 4}</span>
            )}
          </div>
        )}
      </div>

      {/* Footer: date + actions */}
      <div className="px-5 py-3.5 border-t border-gray-100 flex items-center justify-between">
        <span className="text-xs text-gray-400">{formatDate(job.posted_at)}</span>
        <div className="flex items-center gap-2">
          <button
            onClick={e => { e.stopPropagation(); onView() }}
            className="px-3 py-1.5 border border-gray-300 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-50 transition flex items-center gap-1.5"
          >
            <Eye className="w-3 h-3" /> View
          </button>
          {applied ? (
            <span className="text-xs font-semibold text-green-600 flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5" /> Applied
            </span>
          ) : cooldownDate ? (
            <span className="text-xs font-semibold text-orange-600 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> Re-apply {cooldownDate}
            </span>
          ) : (
            <button
              onClick={e => { e.stopPropagation(); onApply() }}
              className={`px-3.5 py-1.5 text-xs font-semibold rounded-lg transition flex items-center gap-1.5 ${
                isInvited
                  ? 'bg-amber-500 hover:bg-amber-600 text-white'
                  : 'bg-blue-900 hover:bg-blue-800 text-white'
              }`}
            >
              <Send className="w-3 h-3" /> {isInvited ? 'Apply (Invited)' : 'Apply'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/* ────────────────── Main Page ──────────────────────────────────── */

const JobMatchingPage: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()

  const [hasPeoAssessment, setHasPeoAssessment] = useState(false)
  const [pendingPeoSurvey, setPendingPeoSurvey] = useState<{ id: string; title: string } | null>(null)
  const [jobs, setJobs] = useState<Job[]>([])
  const [appInfoMap, setAppInfoMap] = useState<Map<string, { status: string; reviewed_at?: string }>>(new Map())
  const [matchScores, setMatchScores] = useState<Map<string, { overall: number; peo: number; skills: number; fit: number; matchingSkills: string[]; matchingOutcomes: string[]; scoreType: string }>>(new Map())
  const [loading, setLoading] = useState(true)

  // Filters
  const [search, setSearch] = useState('')
  const [typeFilters, setTypeFilters] = useState<Set<EmploymentType>>(new Set())
  const [levelFilters, setLevelFilters] = useState<Set<ExperienceLevel>>(new Set())
  const [sortBy, setSortBy] = useState<'newest' | 'deadline' | 'best_match'>('newest')
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [matchingMode, setMatchingMode] = useState(false)
  const [minMatchScore, setMinMatchScore] = useState(0)

  // Modals
  const [applyingJob, setApplyingJob] = useState<Job | null>(null)
  const [viewingJob, setViewingJob] = useState<Job | null>(null)
  const [successJob, setSuccessJob] = useState<Job | null>(null)
  const [companyModalJob, setCompanyModalJob] = useState<Job | null>(null)
  const [pendingInviteCount, setPendingInviteCount] = useState(0)
  const [invitations, setInvitations] = useState<Array<{ job_id: string; status: string }>>([])

  const loadData = useCallback(async () => {
    if (!user?.id) return
    setLoading(true)
    try {
      const [{ data: peoScores }, { data: alumniProfileData }] = await Promise.all([
        supabaseClient.from('alumni_peo_scores').select('id').eq('alumni_id', user.id).limit(1),
        supabaseClient.from('alumni_profiles').select('program').eq('user_id', user.id).single(),
      ])
      setHasPeoAssessment((peoScores && peoScores.length > 0) || false)

      // Find the latest active PEO-linked survey scoped to the alumni's program
      const alumniProgram = alumniProfileData?.program
      const { data: activeSurveysData } = await supabaseClient
        .from('surveys')
        .select('id, title, questions, target_programs')
        .eq('status', 'active')
        .order('created_at', { ascending: false })

      const peoProgramSurveys = (activeSurveysData || []).filter((survey: any) => {
        // Scope: if survey targets specific programs, alumni's program must be included
        const hasProgTarget = survey.target_programs && survey.target_programs.length > 0
        if (hasProgTarget && alumniProgram && !survey.target_programs.includes(alumniProgram)) {
          return false
        }
        // Must have at least one rating question linked to PEO/PO outcomes
        return (survey.questions || []).some((q: any) =>
          q.type === 'rating' && (q.peo_ids?.length > 0 || q.peo_id)
        )
      })

      if (peoProgramSurveys.length > 0) {
        const { data: completedResponses } = await supabaseClient
          .from('survey_responses')
          .select('survey_id')
          .eq('user_id', user.id)
          .in('survey_id', peoProgramSurveys.map((s: any) => s.id))
        const completedIds = new Set((completedResponses || []).map((r: any) => r.survey_id))
        const pending = peoProgramSurveys.find((s: any) => !completedIds.has(s.id))
        setPendingPeoSurvey(pending ? { id: pending.id, title: pending.title } : null)
      } else {
        setPendingPeoSurvey(null)
      }

      const { data: jobsData, error: jobsErr } = await supabaseClient
        .from('jobs').select('*, companies(name, logo_url, description, location, industry, website, company_size_text), users!jobs_posted_by_fkey(full_name, email, employer_profiles(phone, contact_person, position))')
        .eq('status', 'active').is('deleted_at', null)
        .order('posted_at', { ascending: false })
      if (jobsErr) throw jobsErr

      // Fetch aggregate company ratings (safe SECURITY DEFINER RPC — no individual data)
      const { data: ratingsData } = await supabaseClient.rpc('get_company_public_ratings')
      const ratingsMap = new Map<string, { avg_rating: number; rating_count: number }>()
      for (const r of (ratingsData || []) as Array<{ company_id: string; avg_rating: number; rating_count: number }>) {
        ratingsMap.set(r.company_id, { avg_rating: r.avg_rating, rating_count: r.rating_count })
      }

      const loadedJobs = (jobsData || []).map((j: any) => {
        const rating = ratingsMap.get(j.company_id)
        const employerProfile = j.users?.employer_profiles?.[0] ?? j.users?.employer_profiles ?? null
        return {
          ...j,
          company_name: j.companies?.name || 'Unknown Company',
          company_logo: j.companies?.logo_url || null,
          company_description: j.companies?.description || null,
          company_industry: j.companies?.industry || null,
          company_website: j.companies?.website || null,
          company_size_text: j.companies?.company_size_text || null,
          company_location: j.companies?.location || null,
          employer_name: j.users?.full_name || null,
          employer_email: j.users?.email || null,
          employer_phone: employerProfile?.phone || null,
          employer_position: employerProfile?.position || employerProfile?.contact_person || null,
          avg_rating: rating?.avg_rating ?? null,
          rating_count: rating?.rating_count ?? null,
        }
      })
      setJobs(loadedJobs)

      const { data: apps } = await supabaseClient
        .from('job_applications').select('job_id, status, reviewed_at').eq('alumni_id', user.id)
        .order('applied_at', { ascending: false })

      const infoMap = new Map<string, { status: string; reviewed_at?: string }>()
      const activeStatuses = ['pending', 'reviewed', 'shortlisted', 'hired']
      for (const app of apps || []) {
        const existing = infoMap.get(app.job_id)
        if (!existing) {
          infoMap.set(app.job_id, { status: app.status, reviewed_at: app.reviewed_at })
        } else if (!activeStatuses.includes(existing.status) && activeStatuses.includes(app.status)) {
          infoMap.set(app.job_id, { status: app.status, reviewed_at: app.reviewed_at })
        }
      }
      setAppInfoMap(infoMap)

      // Compute THEJMA job match scores via backend ML service
      try {
        const { data: { session } } = await supabaseClient.auth.getSession()
        const token = session?.access_token
        if (!token) throw new Error('No auth session')

        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
        const res = await fetch(`${apiUrl}/api/jobs/matches`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })

        if (!res.ok) throw new Error(`Backend returned ${res.status}`)
        const body = await res.json() as { success: boolean; data: Array<{ job_id: string; overall_score: number; peo_alignment_score: number; skills_score: number; profile_fit_score: number; matching_skills: string[]; matching_outcomes: string[]; score_type: string }> }

        if (body.success && body.data) {
          const scores = new Map<string, { overall: number; peo: number; skills: number; fit: number; matchingSkills: string[]; matchingOutcomes: string[]; scoreType: string }>()
          for (const m of body.data) {
            scores.set(m.job_id, {
              overall: m.overall_score,
              peo: m.peo_alignment_score,
              skills: m.skills_score,
              fit: m.profile_fit_score,
              matchingSkills: m.matching_skills,
              matchingOutcomes: m.matching_outcomes,
              scoreType: m.score_type,
            })
          }
          setMatchScores(scores)
        }
      } catch (matchErr) {
        console.warn('Job matching scores unavailable:', matchErr)
      }
    } catch (err: any) {
      console.error('Failed to load jobs:', err)
      toast.error('Failed to load jobs')
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => { loadData() }, [loadData])

  // Fetch invitations — used for Invitations tab badge AND the "Invited" indicator on job cards
  useEffect(() => {
    if (!user?.id) return
    supabaseClient.rpc('get_my_invitations').then(({ data }) => {
      const list = (data as any[]) || []
      setInvitations(list.map((i: any) => ({ job_id: i.job_id, status: i.status })))
      setPendingInviteCount(list.filter((i: any) => i.status === 'pending').length)
    })
  }, [user?.id])

  const invitedJobIds = new Set(
    invitations.filter(i => i.status === 'pending').map(i => i.job_id)
  )

  // Lock body scroll when any modal is open to prevent layout-shift artifacts
  useEffect(() => {
    if (applyingJob || viewingJob || companyModalJob) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [applyingJob, viewingJob, companyModalJob])

  // Auto-sort when entering/leaving matching mode
  useEffect(() => {
    if (matchingMode && matchScores.size > 0) {
      setSortBy('best_match')
    } else if (!matchingMode) {
      setSortBy('newest')
      setMinMatchScore(0)
    }
  }, [matchingMode])

  /* ── filtering & sorting ───────────────────────────────────── */

  const toggleType = (t: EmploymentType) => {
    setTypeFilters(prev => {
      const next = new Set(prev)
      next.has(t) ? next.delete(t) : next.add(t)
      return next
    })
  }
  const toggleLevel = (l: ExperienceLevel) => {
    setLevelFilters(prev => {
      const next = new Set(prev)
      next.has(l) ? next.delete(l) : next.add(l)
      return next
    })
  }

  const filtered = jobs
    .filter(j => {
      if (matchingMode && minMatchScore > 0) {
        const score = matchScores.get(j.id)?.overall ?? 0
        if (score < minMatchScore) return false
      }
      if (typeFilters.size > 0 && !typeFilters.has(j.employment_type)) return false
      if (levelFilters.size > 0 && !levelFilters.has(j.experience_level)) return false
      if (search) {
        const q = search.toLowerCase()
        return (
          j.title.toLowerCase().includes(q) ||
          j.company_name.toLowerCase().includes(q) ||
          (j.location || '').toLowerCase().includes(q) ||
          (j.skills_required || []).some(s => s.toLowerCase().includes(q))
        )
      }
      return true
    })
    .sort((a, b) => {
      if (sortBy === 'best_match') {
        const sa = matchScores.get(a.id)?.overall ?? 0
        const sb = matchScores.get(b.id)?.overall ?? 0
        return sb - sa
      }
      if (sortBy === 'deadline') {
        if (!a.deadline) return 1
        if (!b.deadline) return -1
        return new Date(a.deadline).getTime() - new Date(b.deadline).getTime()
      }
      return new Date(b.posted_at).getTime() - new Date(a.posted_at).getTime()
    })

  const matchStats = matchScores.size > 0 ? {
    total: matchScores.size,
    excellent: [...matchScores.values()].filter(s => s.overall >= 75).length,
    good: [...matchScores.values()].filter(s => s.overall >= 50 && s.overall < 75).length,
    avg: Math.round([...matchScores.values()].reduce((a, s) => a + s.overall, 0) / matchScores.size),
    top: Math.max(...[...matchScores.values()].map(s => s.overall)),
  } : null

  const JOBS_PER_PAGE = 12
  const [jobPage, setJobPage] = useState(1)
  // Reset to page 1 whenever filters change
  useEffect(() => { setJobPage(1) }, [search, typeFilters, levelFilters, sortBy, matchingMode, minMatchScore])
  const totalJobPages = Math.max(1, Math.ceil(filtered.length / JOBS_PER_PAGE))
  const pagedJobs = filtered.slice((jobPage - 1) * JOBS_PER_PAGE, jobPage * JOBS_PER_PAGE)

  const activeFilterCount = typeFilters.size + levelFilters.size

  /* ── sidebar filter panel (shared desktop + mobile) ────────── */

  const renderFilters = () => (
    <div className="space-y-6">
      {/* Type of Employment */}
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Type of Employment</h3>
        <div className="space-y-2">
          {(Object.entries(TYPE_LABELS) as [EmploymentType, string][]).map(([key, label]) => {
            const count = jobs.filter(j => j.employment_type === key).length
            return (
              <label key={key} className="flex items-center gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={typeFilters.has(key)}
                  onChange={() => toggleType(key)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-900 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900 flex-1">{label}</span>
                <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5 min-w-[24px] text-center">{count}</span>
              </label>
            )
          })}
        </div>
      </div>

      {/* Seniority Level */}
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Seniority Level</h3>
        <div className="space-y-2">
          {(Object.entries(LEVEL_LABELS) as [ExperienceLevel, string][]).map(([key, label]) => {
            const count = jobs.filter(j => j.experience_level === key).length
            return (
              <label key={key} className="flex items-center gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={levelFilters.has(key)}
                  onChange={() => toggleLevel(key)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-900 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900 flex-1">{label}</span>
                <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5 min-w-[24px] text-center">{count}</span>
              </label>
            )
          })}
        </div>
      </div>

      {/* Reset */}
      {activeFilterCount > 0 && (
        <button
          onClick={() => { setTypeFilters(new Set()); setLevelFilters(new Set()) }}
          className="w-full py-2 text-sm font-medium text-red-600 hover:text-red-700 border border-red-200 rounded-lg hover:bg-red-50 transition"
        >
          Reset Filters
        </button>
      )}
    </div>
  )

  /* ── sidebar match dashboard (matching mode) ─────────────── */

  const renderMatchDashboard = () => (
    <div className="space-y-5">
      {/* Score Breakdown */}
      <div>
        <h3 className="text-sm font-bold text-indigo-900 mb-3 uppercase tracking-wide flex items-center gap-1.5">
          <Target className="w-3.5 h-3.5" /> How Scores Work
        </h3>
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-600 flex items-center gap-1">PEO Alignment <InfoTooltip text="Measures how well your program's learning outcomes align with each job's requirements. PEOs (Program Educational Objectives) are broad career goals you're expected to achieve years after graduation; POs (Program Outcomes) are specific skills and competencies you demonstrated upon completing your program." /></span>
              <span className="font-bold text-indigo-600">50%</span>
            </div>
            <div className="h-1.5 bg-indigo-100 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-500 rounded-full" style={{ width: '50%' }} />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-600">Skills Match</span>
              <span className="font-bold text-blue-600">30%</span>
            </div>
            <div className="h-1.5 bg-blue-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full" style={{ width: '30%' }} />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-600">Profile Fit</span>
              <span className="font-bold text-emerald-600">20%</span>
            </div>
            <div className="h-1.5 bg-emerald-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full" style={{ width: '20%' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Score Legend */}
      <div>
        <h3 className="text-sm font-bold text-indigo-900 mb-3 uppercase tracking-wide flex items-center gap-1.5">
          <BarChart3 className="w-3.5 h-3.5" /> Score Legend
        </h3>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-xs text-gray-700">75%+ — Excellent Match</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span className="text-xs text-gray-700">50–74% — Good Match</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-400" />
            <span className="text-xs text-gray-700">Below 50% — Low Match</span>
          </div>
        </div>
      </div>

      {/* Filter by Score */}
      <div>
        <h3 className="text-sm font-bold text-indigo-900 mb-3 uppercase tracking-wide">Filter by Score</h3>
        <div className="space-y-1.5">
          {[
            { label: 'Excellent (75%+)', min: 75, color: 'text-green-600' },
            { label: 'Good (50%+)', min: 50, color: 'text-blue-600' },
            { label: 'All Jobs', min: 0, color: 'text-gray-600' },
          ].map(tier => {
            const count = [...matchScores.values()].filter(s => s.overall >= tier.min).length
            return (
              <button
                key={tier.min}
                onClick={() => setMinMatchScore(tier.min)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition ${
                  minMatchScore === tier.min
                    ? 'bg-indigo-50 border border-indigo-200 font-semibold'
                    : 'hover:bg-gray-50 border border-transparent'
                }`}
              >
                <span className={tier.color}>{tier.label}</span>
                <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">{count}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Exit matching mode */}
      <button
        onClick={() => setMatchingMode(false)}
        className="w-full py-2.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition flex items-center justify-center gap-1.5"
      >
        <Briefcase className="w-3.5 h-3.5" /> Switch to Browse Mode
      </button>
    </div>
  )

  /* ── render ────────────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-900 mx-auto" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* ── Hero Banner ── */}
      <div className={`rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden transition-all duration-500 ${
        matchingMode
          ? 'bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-800'
          : 'bg-blue-900'
      }`}>
        <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
        <div className={`absolute -bottom-8 right-16 w-40 h-40 rounded-full pointer-events-none ${matchingMode ? 'bg-purple-400/10' : 'bg-yellow-400/10'}`} />
        <div className="absolute top-4 right-32 w-16 h-16 bg-white/5 rounded-full pointer-events-none" />
        {matchingMode && <div className="absolute top-6 right-8 w-24 h-24 bg-indigo-400/10 rounded-full pointer-events-none" />}
        <div className="relative">
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2.5">
            {matchingMode && <Sparkles className="w-7 h-7 text-yellow-300" />}
            {matchingMode ? 'Job Matching' : 'Job Opportunities'}
          </h1>
          <p className={`text-sm mt-1 ${matchingMode ? 'text-indigo-200' : 'text-blue-200'}`}>
            {matchingMode
              ? 'Jobs ranked by your PEO assessment and profile alignment'
              : 'Browse and apply for jobs from our partner employers'}
          </p>

          <div className="mt-4 flex flex-wrap gap-3">
            {matchingMode && matchStats ? (
              <>
                <span className="bg-white/10 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
                  <Target className="w-3.5 h-3.5 text-yellow-300" />
                  Top match: {matchStats.top}%
                </span>
                <span className="bg-white/10 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5 text-yellow-300" />
                  {matchStats.excellent} excellent match{matchStats.excellent !== 1 ? 'es' : ''}
                </span>
                <span className="bg-white/10 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
                  <BarChart3 className="w-3.5 h-3.5 text-yellow-300" />
                  Avg score: {matchStats.avg}%
                </span>
              </>
            ) : (
              <>
                <span className="bg-white/10 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
                  <Briefcase className="w-3.5 h-3.5 text-yellow-300" />
                  {jobs.length} active {jobs.length === 1 ? 'job' : 'jobs'}
                </span>
                <span className="bg-white/10 rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-yellow-300" />
                  {[...appInfoMap.values()].filter(i => ['pending','reviewed','shortlisted','hired'].includes(i.status)).length} applied
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Tabs: Browse Jobs / My Applications / Invitations ── */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        <button className="px-4 py-2 rounded-lg text-sm font-medium bg-white shadow-sm text-gray-900 transition">
          <span className="flex items-center gap-2"><Briefcase className="w-4 h-4" /> Browse Jobs</span>
        </button>
        <button
          onClick={() => navigate('/my-applications')}
          className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 transition"
        >
          <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> My Applications</span>
        </button>
        <button
          onClick={() => navigate('/my-applications?tab=invitations')}
          className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 transition whitespace-nowrap"
        >
          <span className="flex items-center gap-2">
            <Send className="w-4 h-4 shrink-0" /> Invitations
            {pendingInviteCount > 0 && (
              <span className="bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none shrink-0">{pendingInviteCount}</span>
            )}
          </span>
        </button>
      </div>

      {/* ── AI Matching Mode Toggle ── */}
      {hasPeoAssessment && matchScores.size > 0 && (
        <div
          className={`rounded-2xl border-2 transition-all duration-300 cursor-pointer select-none ${
            matchingMode
              ? 'bg-gradient-to-r from-indigo-50 via-purple-50 to-indigo-50 border-indigo-300 shadow-lg shadow-indigo-100/50'
              : 'bg-white border-gray-200 hover:border-indigo-200 hover:shadow-md'
          }`}
          onClick={() => setMatchingMode(!matchingMode)}
        >
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
                matchingMode ? 'bg-indigo-600 shadow-lg shadow-indigo-300' : 'bg-gray-100'
              }`}>
                <Sparkles className={`w-5 h-5 transition-colors ${matchingMode ? 'text-white' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className={`text-sm font-bold transition-colors ${matchingMode ? 'text-indigo-900' : 'text-gray-700'}`}>
                  Job Matching
                  {matchingMode && <span className="ml-1.5 text-[10px] font-bold bg-indigo-600 text-white px-1.5 py-0.5 rounded-full align-middle">ACTIVE</span>}
                </p>
                <p className="text-xs text-gray-500">
                  {matchingMode ? 'Jobs ranked by your PEO assessment alignment' : 'Enable to see personalized match scores and rankings'}
                </p>
              </div>
            </div>
            <div
              className={`relative w-14 h-7 rounded-full transition-all duration-300 shrink-0 ${
                matchingMode ? 'bg-indigo-600' : 'bg-gray-300'
              }`}
              onClick={e => { e.stopPropagation(); setMatchingMode(!matchingMode) }}
            >
              <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-all duration-300 ${
                matchingMode ? 'left-7' : 'left-0.5'
              }`} />
            </div>
          </div>
          {matchingMode && matchStats && (
            <div className="px-4 pb-4 -mt-1">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                <div className="bg-white/80 backdrop-blur rounded-xl p-3 text-center border border-indigo-100">
                  <p className="text-xl font-bold text-indigo-600">{matchStats.top}%</p>
                  <p className="text-[10px] text-gray-500 font-medium mt-0.5">Top Match</p>
                </div>
                <div className="bg-white/80 backdrop-blur rounded-xl p-3 text-center border border-green-100">
                  <p className="text-xl font-bold text-green-600">{matchStats.excellent}</p>
                  <p className="text-[10px] text-gray-500 font-medium mt-0.5">Excellent (75%+)</p>
                </div>
                <div className="bg-white/80 backdrop-blur rounded-xl p-3 text-center border border-blue-100">
                  <p className="text-xl font-bold text-blue-600">{matchStats.good}</p>
                  <p className="text-[10px] text-gray-500 font-medium mt-0.5">Good (50%+)</p>
                </div>
                <div className="bg-white/80 backdrop-blur rounded-xl p-3 text-center border border-gray-100">
                  <p className="text-xl font-bold text-gray-600">{matchStats.avg}%</p>
                  <p className="text-[10px] text-gray-500 font-medium mt-0.5">Avg Score</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* PEO Assessment Banner — hidden in matching mode */}
      {!matchingMode && (
        <>
          {/* No assessment yet */}
          {!hasPeoAssessment && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-yellow-400 flex items-center justify-center shrink-0">
                <Lock className="w-4 h-4 text-gray-900" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">Complete a PEO Assessment to unlock job matching</p>
                <p className="text-xs text-gray-600 mt-0.5">You can still browse and apply, but match scores are unavailable.</p>
              </div>
              {pendingPeoSurvey && (
                <button
                  onClick={() => navigate(`/survey?id=${pendingPeoSurvey.id}`)}
                  className="shrink-0 text-xs bg-blue-900 text-white px-3 py-2 rounded-lg hover:bg-blue-800 transition font-semibold flex items-center gap-1.5"
                >
                  <FileText className="w-3 h-3" /> Take Assessment
                </button>
              )}
            </div>
          )}

          {/* Assessment done, but a new/redeployed PEO survey for their program is available */}
          {hasPeoAssessment && pendingPeoSurvey && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-indigo-500 flex items-center justify-center shrink-0">
                <Bell className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">New PEO assessment available for your program</p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Retake <span className="font-medium">{pendingPeoSurvey.title}</span> to update your scores and improve your job match accuracy.
                </p>
              </div>
              <button
                onClick={() => navigate(`/survey?id=${pendingPeoSurvey.id}`)}
                className="shrink-0 text-xs bg-indigo-600 text-white px-3 py-2 rounded-lg hover:bg-indigo-700 transition font-semibold flex items-center gap-1.5"
              >
                <FileText className="w-3 h-3" /> Update Scores
              </button>
            </div>
          )}

          {/* Assessment done and up to date */}
          {hasPeoAssessment && !pendingPeoSurvey && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-medium text-green-800">PEO Assessment Complete — AI matching enabled</span>
            </div>
          )}
        </>
      )}

      {/* ── Search Bar ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by job title, company, skills, or location..."
              className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value as 'newest' | 'deadline' | 'best_match')}
            className="border border-gray-300 rounded-xl pl-3 pr-8 py-2.5 text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 hidden sm:block"
          >
            <option value="newest">Sort: Newest Post</option>
            <option value="deadline">Sort: Deadline</option>
            {matchScores.size > 0 && <option value="best_match">Sort: Best Match</option>}
          </select>
          {/* Mobile filter toggle */}
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="lg:hidden flex items-center gap-1.5 px-3 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-700 hover:bg-gray-50"
          >
            {matchingMode ? <Sparkles className="w-4 h-4" /> : <SlidersHorizontal className="w-4 h-4" />}
            {matchingMode ? 'Match Info' : 'Filters'}
            {!matchingMode && activeFilterCount > 0 && (
              <span className="bg-blue-900 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{activeFilterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Mobile filters (collapsible) ── */}
      {showMobileFilters && (
        <div className={`lg:hidden rounded-2xl border shadow-sm p-5 ${
          matchingMode ? 'bg-gradient-to-b from-indigo-50 to-white border-indigo-200' : 'bg-white border-gray-200'
        }`}>
          {matchingMode ? renderMatchDashboard() : renderFilters()}
        </div>
      )}

      {/* ── Main Layout: Sidebar + Grid ── */}
      <div className="flex gap-6">
        {/* Left sidebar — desktop only */}
        <aside className="hidden lg:block w-64 shrink-0">
          <div className={`rounded-2xl border shadow-sm p-5 sticky top-6 transition-all duration-300 ${
            matchingMode ? 'bg-gradient-to-b from-indigo-50 to-white border-indigo-100' : 'bg-white border-gray-100'
          }`}>
            {matchingMode ? renderMatchDashboard() : renderFilters()}
          </div>
        </aside>

        {/* Right: results */}
        <div className="flex-1 min-w-0">
          {/* Results count */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">
              <span className="text-2xl font-bold text-gray-900">{filtered.length.toLocaleString()}</span>
              {matchingMode
                ? ` Job${filtered.length !== 1 ? 's' : ''} Ranked`
                : ` Job${filtered.length !== 1 ? 's' : ''} Found`}
            </p>
          </div>

          {/* Job Grid */}
          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-gray-300" />
              </div>
              <h3 className="text-lg font-bold text-gray-700">No jobs found</h3>
              <p className="text-sm text-gray-400 mt-1">
                {search || activeFilterCount > 0 ? 'Try adjusting your search or filters.' : 'Check back later for new opportunities.'}
              </p>
            </div>
          ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {pagedJobs.map(job => {
                const info = appInfoMap.get(job.id)
                const isActive = info && ['pending', 'reviewed', 'shortlisted', 'hired'].includes(info.status)
                const inCooldown = info?.status === 'rejected' && info.reviewed_at &&
                  (new Date(info.reviewed_at).getTime() + COOLDOWN_MS > Date.now())
                const cooldownDate = inCooldown
                  ? new Date(new Date(info!.reviewed_at!).getTime() + COOLDOWN_MS)
                      .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : undefined
                return (
                  <JobCard
                    key={job.id}
                    job={job}
                    applied={!!isActive}
                    cooldownDate={cooldownDate}
                    isInvited={invitedJobIds.has(job.id)}
                    matchScore={hasPeoAssessment ? matchScores.get(job.id)?.overall : undefined}
                    matchingSkills={hasPeoAssessment ? matchScores.get(job.id)?.matchingSkills : undefined}
                    onApply={() => setApplyingJob(job)}
                    onView={() => setViewingJob(job)}
                    onCompanyClick={() => setCompanyModalJob(job)}
                  />
                )
              })}
            </div>

            {/* Pagination controls */}
            {totalJobPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <button
                  onClick={() => setJobPage(p => Math.max(1, p - 1))}
                  disabled={jobPage === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  ← Prev
                </button>
                {Array.from({ length: totalJobPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalJobPages || Math.abs(p - jobPage) <= 2)
                  .reduce<(number | '…')[]>((acc, p, idx, arr) => {
                    if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('…')
                    acc.push(p)
                    return acc
                  }, [])
                  .map((p, i) =>
                    p === '…' ? (
                      <span key={`ellipsis-${i}`} className="px-2 text-gray-400 text-sm">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setJobPage(p as number)}
                        className={`px-3 py-1.5 text-sm rounded-lg border transition ${
                          p === jobPage
                            ? 'bg-blue-900 text-white border-blue-900 font-semibold'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )
                }
                <button
                  onClick={() => setJobPage(p => Math.min(totalJobPages, p + 1))}
                  disabled={jobPage === totalJobPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  Next →
                </button>
              </div>
            )}
            </>
          )}
        </div>
      </div>

      {/* View modal */}
      {viewingJob && (() => {
        const info = appInfoMap.get(viewingJob.id)
        const isActive = info && ['pending', 'reviewed', 'shortlisted', 'hired'].includes(info.status)
        const inCooldown = info?.status === 'rejected' && info.reviewed_at &&
          (new Date(info.reviewed_at).getTime() + COOLDOWN_MS > Date.now())
        const cooldownDate = inCooldown
          ? new Date(new Date(info!.reviewed_at!).getTime() + COOLDOWN_MS)
              .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          : undefined
        return (
          <ViewJobModal
            job={viewingJob}
            applied={!!isActive}
            cooldownDate={cooldownDate}
            onClose={() => setViewingJob(null)}
            onApply={() => { setViewingJob(null); setApplyingJob(viewingJob) }}
          />
        )
      })()}

      {/* Apply modal */}
      {applyingJob && (
        <ApplyModal
          job={applyingJob}
          isInvited={invitedJobIds.has(applyingJob.id)}
          onClose={() => setApplyingJob(null)}
          onApplied={() => {
            setAppInfoMap(prev => {
              const next = new Map(prev)
              next.set(applyingJob.id, { status: 'pending' })
              return next
            })
            const appliedJob = applyingJob
            setApplyingJob(null)
            setSuccessJob(appliedJob)
          }}
        />
      )}

      {/* Application success modal */}
      {successJob && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Coloured top strip */}
            <div className="bg-blue-900 px-6 pt-8 pb-6 text-white text-center">
              <div className="w-14 h-14 bg-yellow-400 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-7 h-7 text-blue-900" />
              </div>
              <h2 className="text-xl font-bold">Application Submitted!</h2>
              <p className="text-blue-200 text-sm mt-1">{successJob.title}</p>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-900">Wait for the employer to reach out</p>
                  <p className="text-sm text-blue-700 mt-0.5">
                    The employer will review your application and contact you by <strong>email</strong> if they're interested. Check your inbox regularly.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <FileText className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-800">Track your application status</p>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Visit <strong>My Applications</strong> to see if your status changes to Under Review, Shortlisted, or Hired.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 pb-6 flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => { setSuccessJob(null); navigate('/my-applications') }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-900 text-white rounded-xl text-sm font-semibold hover:bg-blue-800 transition"
              >
                <FileText className="w-4 h-4" /> View My Applications
              </button>
              <button
                onClick={() => setSuccessJob(null)}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
              >
                Continue Browsing
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Company info modal */}
      {companyModalJob && (
        <CompanyInfoModal job={companyModalJob} onClose={() => setCompanyModalJob(null)} />
      )}
    </div>
  )
}

export default JobMatchingPage